/* eslint-disable react/no-did-update-set-state */
import * as React from 'react';
import classnames from 'classnames';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import CollapsibleContainer from 'components/collapsible/container/CollapsibleContainer';

import { getElementInfo } from 'utils/element';

import './collapsible-simple.scss';

type Props = {
  title?: string,
  isExpanded?: boolean,
  isDisabled?: boolean,
  children: React.ReactNode,
  withBorder?: boolean,
  containerClassName?: string,
  headerClassName?: string,
  titleClassName?: string,
  contentClassName?: string,
  iconClassName?: string,
  onToggle?: (a: boolean) => void
};

type State = {
  isExpanded: boolean,
  isDisabled: boolean
};

class CollapsibleSimple extends React.PureComponent<Props, State> {
  state = {
    isExpanded: this.props.isExpanded || false,
    isDisabled: this.props.isDisabled || false
  };

  contentRef: React.RefObject<any> = React.createRef();

  componentDidUpdate(prevProps: Props) {
    const { isExpanded, isDisabled } = this.props;

    if (isExpanded !== prevProps.isExpanded) {
      this.setState({
        isExpanded: Boolean(isExpanded)
      });
    }

    if (isDisabled !== prevProps.isDisabled) {
      this.setState({
        isDisabled: Boolean(isDisabled)
      });
    }
  }

  handleToggle = (): void => {
    const { onToggle } = this.props;

    this.setState(state => ({
      isExpanded: !state.isExpanded
    }), () => {
      const { isExpanded } = this.state;

      if (onToggle) {
        onToggle(isExpanded);
      }
    });
  };

  renderIcon(): React.ReactNode {
    const { iconClassName } = this.props;
    const { isExpanded, isDisabled } = this.state;

    const iconClasses = classnames('collapsible-simple__icon', iconClassName, {
      'collapsible-simple__icon--is-expanded': isExpanded,
      'collapsible-simple__icon--is-disabled': isDisabled
    });

    return (
      <div className={iconClasses}>
        <SVGDynamicIcon
          name="chevron-down-custom-fg"
          width={16}
          height={16}
          customColor="grey"
        />
      </div>
    );
  }

  renderTitle(): React.ReactNode {
    const { title, titleClassName } = this.props;

    const titleClasses = classnames('collapsible-simple__title', titleClassName, {
      'text-overflow-ellipsis': true
    });

    return (
      <div className={titleClasses}>
        {title}
      </div>
    );
  }

  renderHeader(): React.ReactNode {
    const { withBorder, headerClassName } = this.props;
    const { isExpanded, isDisabled } = this.state;

    const headerClasses = classnames('collapsible-simple__header', headerClassName, {
      'collapsible-simple__header--is-expanded': isExpanded,
      'collapsible-simple__header--with-border': Boolean(withBorder),
      'collapsible-simple__header--is-disabled': isDisabled
    });

    return (
      <div
        className={headerClasses}
        onClick={this.handleToggle}
      >
        {this.renderTitle()}
        {this.renderIcon()}
      </div>
    );
  }

  renderContent(): React.ReactNode | undefined | null {
    const { children, contentClassName } = this.props;
    const { isExpanded } = this.state;
    const contentInfo = getElementInfo(this.contentRef.current);
    const className = classnames('collapsible-simple__content', contentClassName);

    return (
      <CollapsibleContainer
        contentClassName={className}
        isExpanded={isExpanded}
        heightOverride={contentInfo?.height}
      >
        <div ref={this.contentRef}>
          {children}
        </div>
      </CollapsibleContainer>
    );
  }

  render(): React.ReactNode {
    const { withBorder, containerClassName, title } = this.props;

    const containerClasses = classnames('collapsible-simple', containerClassName, {
      'collapsible-simple--with-border': Boolean(withBorder)
    });

    return (
      <div className={containerClasses}>
        {title && this.renderHeader()}
        {this.renderContent()}
      </div>
    );
  }
}

export default CollapsibleSimple;
